import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { circuitApi } from '../utils/api';

export default function TeamCircuitHistory() {
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year') || new Date().getFullYear();
  const region = searchParams.get('region') || '';

  const [history, setHistory] = useState([]);
  const [teamInfo, setTeamInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [teamId, year, region]);

  async function fetchHistory() {
    setLoading(true);
    try {
      const params = { year };
      if (region) params.region = region;

      const { data } = await circuitApi.getTeamHistory(teamId, params);
      setHistory(data.data.history);

      // Fetch team info from standings for display
      try {
        const standingsRes = await circuitApi.getStandings({ year, region });
        const team = standingsRes.data.data.standings.find(t => t.team_id === teamId);
        if (team) setTeamInfo(team);
      } catch {}
    } catch (err) {
      console.error('Fetch team history error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-secondary)' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            Loading team history...
          </div>
        </div>
      </div>
    );
  }

  const totalPoints = history.reduce((sum, h) => sum + parseFloat(h.circuit_points), 0);
  const avgPlacement = history.length > 0
    ? history.reduce((sum, h) => sum + h.placement, 0) / history.length
    : 0;
  const bestFinish = history.length > 0 ? Math.min(...history.map(h => h.placement)) : null;

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <Link to="/circuit" style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'inline-block', marginBottom: 12 }}>
            &larr; Back to Circuit Standings
          </Link>
          <h1>{teamInfo?.team_name || 'Team'}</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Circuit History - {year} Season{region ? ` · ${region}` : ''}</p>
        </div>

        {/* Summary Card */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 20 }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Total Points
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--amber)' }}>
                {totalPoints.toFixed(1)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Events
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {history.length}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Best Finish
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: bestFinish === 1 ? 'var(--amber)' : 'var(--text-primary)' }}>
                {bestFinish ? ordinal(bestFinish) : '\u2014'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Avg Finish
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {avgPlacement > 0 ? avgPlacement.toFixed(1) : '\u2014'}
              </div>
            </div>
          </div>
        </div>

        {/* Event History */}
        {history.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h3 style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>No circuit results yet</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              This team hasn't competed in any tournaments for {year}.
            </p>
          </div>
        ) : (
          <div>
            <h3 style={{ marginBottom: 16, fontSize: '1.1rem' }}>Event History</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {history.map((event) => {
                const isTopThree = event.placement <= 3;
                const rankColor = event.placement === 1 ? 'var(--amber)' : event.placement === 2 ? '#c0c0c0' : event.placement === 3 ? '#cd7f32' : 'var(--text-primary)';

                return (
                  <div
                    key={event.id}
                    className="card"
                    style={{
                      background: isTopThree ? 'rgba(232,168,56,0.08)' : 'var(--navy-card)',
                      border: `1px solid ${isTopThree ? 'var(--amber)' : 'var(--border)'}`,
                      padding: 16
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      {/* Placement Badge */}
                      <div
                        style={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: isTopThree ? rankColor : 'var(--border)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <span style={{ fontSize: '1.5rem', fontWeight: 700, color: isTopThree ? 'var(--navy-deep)' : 'var(--text-primary)' }}>
                          {event.placement}
                        </span>
                      </div>

                      {/* Event Details */}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                          {event.event_name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' \u00B7 '}
                          {ordinal(event.placement)} of {event.total_teams} teams
                        </div>
                      </div>

                      {/* Points Breakdown */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--amber)', marginBottom: 2 }}>
                          {parseFloat(event.circuit_points).toFixed(1)} pts
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {parseFloat(event.base_points).toFixed(0)} base x {parseFloat(event.field_multiplier).toFixed(2)} + {parseFloat(event.participation_bonus).toFixed(0)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper: Convert number to ordinal (1st, 2nd, 3rd, etc.)
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
